import { App, DashboardRole, HealthcareProvider, HealthcareProvidersState, Nullable, User, UserResponseKeysState } from '../interfaces';
import { PresentedData, PresentedView, Presenter } from './Presentation';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { hasDashboardFeature, isRootOrSuperRole } from '../DashboardAccess';

type CommsCampaignRunRecord = {
    id: string;
    triggerSource: string;
    ranAt: string;
    smsSent: number;
    emailSent: number;
    pushSent: number;
};

export class ToolsPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private healthcareProviders: Array<HealthcareProvider> = [];
    private healthcareProviderId: string;
    private dashboardRole: DashboardRole = 'unauthenticated';
    private userResponseKeys: Array<string> = [];
    private commsSenderEmail: string = '';
    private commsReplyToEmail: string = '';
    private hasResendApiKey: boolean = false;
    private commsCampaignHourlyEnabled: boolean = true;
    private commsCampaignRuns: Array<CommsCampaignRunRecord> = [];

    constructor(options: { app: App; healthcareProviderId: string }) {
        const { app, healthcareProviderId } = options;
        if (!app) {
            throw new Error('ToolsPresenter(): missing app parameter');
        }

        this.app = app;
        this.healthcareProviderId = healthcareProviderId;
        this.observeAuthenticatedUser();
        this.observeHealthcareProviders();
        this.observeUserResponseKeys();
    }

    setHealthcareProviderId(healthcareProviderId: string) {
        this.healthcareProviderId = healthcareProviderId;
        if (this.hasDevelopmentAccess()) {
            void this.fetchCommsEmailSettings();
        }
    }

    private observeAuthenticatedUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                this.dashboardRole = user.dashboardRole;
                if (this.hasDevelopmentAccess()) {
                    void this.fetchCommsEmailSettings();
                }
                this.safeUpdatePresentedData();
            }
        });
    }

    private hasDevelopmentAccess(): boolean {
        const provider = this.healthcareProviders.find((candidateProvider) =>
            candidateProvider.id === this.healthcareProviderId
        );
        return isRootOrSuperRole(this.dashboardRole) &&
            hasDashboardFeature(provider, this.dashboardRole, 'development');
    }

    private requireDevelopmentAccess() {
        if (!this.hasDevelopmentAccess()) {
            throw new Error('Your role does not have access to development features.');
        }
    }

    private observeHealthcareProviders() {
        this.app.getData().queryHealthcareProviders().observe({
            changed: (healthcareProvidersState: HealthcareProvidersState) => {
                this.healthcareProviders = healthcareProvidersState.healthcareProviders;
                if (this.hasDevelopmentAccess()) {
                    void this.fetchCommsEmailSettings();
                }
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeUserResponseKeys() {
        this.app.getData().queryUserResponseKeys().observe({
            changed: (userResponseKeysState: UserResponseKeysState) => {
                this.userResponseKeys = userResponseKeysState.keys;
                this.safeUpdatePresentedData();
            }
        });
    }

    private async fetchCommsEmailSettings() {
        try {
            const functions = getFunctions();
            const getCommsEmailSettings = httpsCallable<
                Record<string, never>,
                {
                    senderEmail: string;
                    replyToEmail: string;
                    hourlyEnabled: boolean;
                    hasResendApiKey: boolean;
                    recentRuns: Array<CommsCampaignRunRecord>;
                }
            >(functions, 'getCommsEmailSettings');

            const result = await getCommsEmailSettings({});
            this.commsSenderEmail = result.data.senderEmail;
            this.commsReplyToEmail = result.data.replyToEmail;
            this.commsCampaignHourlyEnabled = result.data.hourlyEnabled;
            this.hasResendApiKey = result.data.hasResendApiKey;
            this.commsCampaignRuns = result.data.recentRuns;
            this.safeUpdatePresentedData();
        } catch (error) {
            console.error('TOOLS_PRESENTER: Error fetching comms email settings:', error);
        }
    }

    presentedData(): PresentedData {
        return {
            isRoot: this.dashboardRole === 'root',
            isPlatformOperator: isRootOrSuperRole(this.dashboardRole),
            healthcareProviders: this.healthcareProviders,
            userResponseKeys: this.userResponseKeys,
            commsSenderEmail: this.commsSenderEmail,
            commsReplyToEmail: this.commsReplyToEmail,
            hasResendApiKey: this.hasResendApiKey,
            commsCampaignHourlyEnabled: this.commsCampaignHourlyEnabled,
            commsCampaignRuns: this.commsCampaignRuns,
            onDownloadAssessmentAnswers: async (filters: { healthcareProviderId?: string; key?: string }) =>
                {
                    this.requireDevelopmentAccess();
                    return this.app.getData().fetchUserResponsesForTools(filters);
                },
            onUpdateCommsEmailSettings: async (settings: { senderEmail: string; replyToEmail: string; resendApiKey?: string }) => {
                this.requireDevelopmentAccess();
                const functions = getFunctions();
                const setCommsEmailSettings = httpsCallable<
                    {
                        senderEmail: string;
                        replyToEmail: string;
                        resendApiKey?: string;
                    },
                    {
                        senderEmail: string;
                        replyToEmail: string;
                        hourlyEnabled: boolean;
                        hasResendApiKey: boolean;
                    }
                >(functions, 'setCommsEmailSettings');

                const result = await setCommsEmailSettings(settings);
                this.commsSenderEmail = result.data.senderEmail;
                this.commsReplyToEmail = result.data.replyToEmail;
                this.commsCampaignHourlyEnabled = result.data.hourlyEnabled;
                this.hasResendApiKey = result.data.hasResendApiKey;
                this.safeUpdatePresentedData();
            },
            onSetCommsCampaignHourlyEnabled: async (hourlyEnabled: boolean) => {
                this.requireDevelopmentAccess();
                const functions = getFunctions();
                const setCommsCampaignHourlyEnabled = httpsCallable<
                    {
                        hourlyEnabled: boolean;
                    },
                    {
                        hourlyEnabled: boolean;
                    }
                >(functions, 'setCommsCampaignHourlyEnabled');

                const result = await setCommsCampaignHourlyEnabled({ hourlyEnabled });
                this.commsCampaignHourlyEnabled = result.data.hourlyEnabled;
                this.safeUpdatePresentedData();
            },
            onRunCommsCampaigns: async () => {
                this.requireDevelopmentAccess();
                const functions = getFunctions();
                const runCommsCampaignsTool = httpsCallable<
                    Record<string, never>,
                    {
                        scannedClients: number;
                        clientsWithCampaign: number;
                        dueMessages: number;
                        smsSent: number;
                        emailSent: number;
                        smsSkippedMissingPhone: number;
                        emailSkippedMissingEmail: number;
                        emailSkippedMissingSender: number;
                        emailSkippedMissingApiKey: number;
                        pushSkippedUnsupported: number;
                        alreadyProcessed: number;
                        skippedMissingTriggerTime: number;
                        skippedNoMatchingCampaign: number;
                        errors: number;
                    }
                >(functions, 'runCommsCampaignsTool');

                const result = await runCommsCampaignsTool({});
                await this.fetchCommsEmailSettings();
                return result.data;
            },
        };
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        if (this.view) {
            this.view.updatePresentedData(this.presentedData());
        }
    }
}
